import { useMatchRoute } from '@tanstack/react-router';
import { useAtomValue } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

/**
 * Whether the sidebar desires to float over the page layout, as opposed to being pinned open as a full-height column.
 */
export const sidebarWantsToFloatAtom = atomWithStorage('sidebar-wants-to-float', false, undefined, {
	getOnInit: true,
});

/** The route families in which the sidebar is allowed to float instead of being fixed. */
const FLOAT_ELIGIBLE_ROUTES = [
	'/orgs/$orgName/projects/$projectId/revisions/$revisionId',
	'/package-store/$packageFamilyId',
	'/wizard',
] as const;

/**
 * Whether the current route allows the sidebar to collapse to the floating layout. If not, the sidebar is forced to its
 * "fixed" position.
 *
 * @returns True when the sidebar is allowed to float.
 */
export function useSidebarMayFloat(): boolean {
	const matchRoute = useMatchRoute();

	return FLOAT_ELIGIBLE_ROUTES.some((to) => matchRoute({ to, fuzzy: true }) !== false);
}

/**
 * Whether the sidebar is in its floating layout or not. Is a derivative of `sidebarWantsToFloat` (does it want to
 * float?) and `sidebarMayFloat` (is it allowed to float?).
 *
 * @returns True when the sidebar is in its floating layout, false when it is in its fixed layout.
 */
export function useSidebarIsFloating(): boolean {
	const sidebarWantsToFloat = useAtomValue(sidebarWantsToFloatAtom);
	const sidebarMayFloat = useSidebarMayFloat();

	return sidebarWantsToFloat && sidebarMayFloat;
}
